import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Avatar, Box, IconButton, InputAdornment, Typography } from '@mui/material';

import { FuncionarioMin, VALOR_MAXIMO_TRANSFERENCIA } from '../../../models/Transferencia.model';
import { Colors } from '../../../shared/colors';
import Currency from '../../components/Currency';
import { NumericInput } from '../../components/Form/NumericInput';

interface Props {
  funcionario: FuncionarioMin;
  index: number;
  onRemove: () => void;
  tipo: 'envio' | 'estorno';
  valor: number;
}

const UsuarioTransferenciaCard = ({ funcionario, index, onRemove, tipo, valor }: Props) => {
  const palavras = funcionario.nome?.trim().split(/\s+/).filter(Boolean) ?? [];
  const iniciais = palavras.length
    ? `${palavras[0].charAt(0)}${
        palavras.length > 1 ? palavras[palavras.length - 1].charAt(0) : ''
      }`.toUpperCase()
    : 'U';
  const corValor =
    Number.isFinite(valor) && valor > 0 ? (tipo === 'envio' ? '#2E7D32' : '#C62828') : '#667085';

  return (
    <Box
      p={1.5}
      mb={1.5}
      borderRadius={1.5}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E1E8F1',
        boxShadow: '0 2px 8px rgba(16, 42, 86, 0.06)'
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Avatar
          sx={{
            backgroundColor: '#EAF2FF',
            color: '#174C9E',
            fontSize: 18,
            fontWeight: 700,
            height: 56,
            width: 56
          }}
        >
          {iniciais}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography color={Colors.secondary} fontSize={14} fontWeight={700} minWidth={0}>
              {funcionario.nome || 'Usuário'}
            </Typography>
            <IconButton
              aria-label={`Remover ${funcionario.nome || 'usuário'}`}
              onClick={onRemove}
              size="small"
            >
              <Icon path={mdiClose} size={0.7} color={Colors.gray1} />
            </IconButton>
          </Box>
          <NumericInput
            name={`funcionarios.${index}.valor`}
            size="small"
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            isAllowed={({ floatValue }) =>
              floatValue === undefined || floatValue <= VALOR_MAXIMO_TRANSFERENCIA
            }
            decimalSeparator=","
            thousandSeparator="."
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    alignSelf: 'stretch',
                    backgroundColor: '#F5F6F8',
                    borderRight: '1px solid #DDE2E8',
                    height: 'auto',
                    maxHeight: 'none',
                    ml: -1.75,
                    mr: 1.5,
                    px: 1.25,
                    '& .MuiTypography-root': { color: corValor, fontSize: 12, fontWeight: 400 }
                  }}
                >
                  R$
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                borderRadius: 1,
                height: 36,
                overflow: 'hidden',
                '& fieldset': { borderColor: '#C8D0DA' },
                '&:hover fieldset': { borderColor: '#C8D0DA' },
                '&.Mui-focused fieldset': { borderColor: '#C8D0DA', borderWidth: 1 }
              },
              '& .MuiInputBase-input': { color: corValor, fontSize: 13, fontWeight: 400 },
              '& .MuiInputBase-input::placeholder': { color: '#98A2B3', opacity: 1 }
            }}
            placeholder={tipo === 'envio' ? 'Informe o valor' : 'Informe o valor a estornar'}
          />
          <Typography color={Colors.gray1} fontSize={11} mt={0.5}>
            Saldo atual: <Currency value={funcionario.saldo ?? 0} />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default UsuarioTransferenciaCard;
